import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Settings,
  Loader2,
  ArrowLeft,
  Check,
  Palette
} from "lucide-react";
import { API_URL } from "@/config/api";

// Board theme previews
const BOARD_THEMES = [
  { id: "classic", name: "Classic Green", light: "#e8d5b7", dark: "#769656" },
  { id: "wood", name: "Wood", light: "#f0d9b5", dark: "#b58863" },
  { id: "blue", name: "Ocean Blue", light: "#dee3e6", dark: "#5d8aa8" },
  { id: "dark", name: "Dark Mode", light: "#4a4a4a", dark: "#2d2d2d" },
  { id: "purple", name: "Purple", light: "#e8d5eb", dark: "#7b5a9e" },
  { id: "tournament", name: "Tournament", light: "#eeeed2", dark: "#769656" },
  { id: "ice", name: "Ice", light: "#e8f4f8", dark: "#4c9eb8" }
];

const PIECE_STYLES = [
  { id: "classic", name: "Classic", description: "Standard look" },
  { id: "neo", name: "Neo", description: "Bold shadows" },
  { id: "modern", name: "Modern", description: "Subtle shadows" },
  { id: "neon", name: "Neon", description: "Glow effect" }
];

// Mini chess board preview component
const BoardPreview = ({ theme, pieceStyle, selected, onClick }) => {
  const squares = [];
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      const isLight = (row + col) % 2 === 0;
      squares.push(
        <div
          key={`${row}-${col}`}
          style={{ backgroundColor: isLight ? theme.light : theme.dark }}
          className="aspect-square flex items-center justify-center"
        >
          {((row === 0 && col === 1) || (row === 3 && col === 2)) && (
            <span className={`text-lg ${
              row === 0 ? "text-zinc-900" : "text-white"
            } ${pieceStyle === "neon" ? "drop-shadow-[0_0_3px_currentColor]" : 
                pieceStyle === "neo" ? "drop-shadow-[2px_2px_2px_rgba(0,0,0,0.5)]" : ""
            }`}>
              {row === 0 ? "♛" : "♕"}
            </span>
          )}
        </div>
      );
    }
  }

  return (
    <button
      onClick={onClick}
      className={`
        relative rounded-lg overflow-hidden border-2 transition-all
        ${selected ? "border-primary ring-2 ring-primary/30" : "border-border/50 hover:border-border"}
      `}
      data-testid={`theme-${theme.id}`}
    >
      <div className="grid grid-cols-4 w-24 h-24">
        {squares}
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-background/80 backdrop-blur-sm py-1 px-2">
        <p className="text-xs font-medium truncate">{theme.name}</p>
      </div>
      {selected && (
        <div className="absolute top-1 right-1 h-5 w-5 bg-primary rounded-full flex items-center justify-center">
          <Check className="h-3 w-3 text-primary-foreground" />
        </div>
      )}
    </button>
  );
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useStore();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    board_theme: "classic",
    piece_style: "classic"
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        // Apply theme to document
        document.documentElement.setAttribute("data-board-theme", data.board_theme);
        document.documentElement.setAttribute("data-piece-style", data.piece_style);
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newSettings)
      });
      
      if (res.ok) {
        toast.success(t("common.success"));
        // Apply theme to document
        document.documentElement.setAttribute("data-board-theme", newSettings.board_theme);
        document.documentElement.setAttribute("data-piece-style", newSettings.piece_style);
      } else {
        const data = await res.json();
        toast.error(data.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = (themeId) => {
    const newSettings = { ...settings, board_theme: themeId };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const handlePieceStyleChange = (styleId) => {
    const newSettings = { ...settings, piece_style: styleId };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const selectedTheme = BOARD_THEMES.find(t => t.id === settings.board_theme) || BOARD_THEMES[0];

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
      {/* Back button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate("/profile")}
        className="mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t("common.back")}
      </Button>

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-black tracking-tight mb-2">
          {t("settings.title")}
        </h1>
        <p className="text-muted-foreground">
          {t("settings.description")}
        </p>
      </div>

      {/* Board Theme */}
      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary" />
            {t("settings.boardTheme")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 justify-center">
            {BOARD_THEMES.map((theme) => (
              <BoardPreview
                key={theme.id}
                theme={theme}
                pieceStyle={settings.piece_style}
                selected={settings.board_theme === theme.id}
                onClick={() => handleThemeChange(theme.id)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Piece Style */}
      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            {t("settings.pieceStyle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {PIECE_STYLES.map((style) => (
              <button
                key={style.id}
                onClick={() => handlePieceStyleChange(style.id)}
                data-testid={`piece-style-${style.id}`}
                className={`
                  p-4 rounded-lg border-2 transition-all text-center
                  ${settings.piece_style === style.id 
                    ? "border-primary bg-primary/10" 
                    : "border-border/50 hover:border-border"
                  }
                `}
              >
                <div 
                  className={`
                    text-4xl mb-2
                    ${style.id === "neon" ? "drop-shadow-[0_0_6px_#d4af37]" : 
                      style.id === "neo" ? "drop-shadow-[3px_3px_3px_rgba(0,0,0,0.6)]" :
                      style.id === "modern" ? "drop-shadow-[1px_1px_1px_rgba(0,0,0,0.3)]" : ""
                    }
                  `}
                  style={{ color: selectedTheme.dark }}
                >
                  ♛
                </div>
                <p className="font-medium text-sm">{style.name}</p>
                <p className="text-xs text-muted-foreground">{style.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Live Preview */}
      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">{t("settings.preview")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="w-full max-w-sm mx-auto rounded-lg overflow-hidden border-2 border-border"
            data-board-theme={settings.board_theme}
            data-piece-style={settings.piece_style}
          >
            <div className="grid grid-cols-8">
              {Array.from({ length: 64 }).map((_, i) => {
                const row = Math.floor(i / 8);
                const col = i % 8;
                const isLight = (row + col) % 2 === 0;
                
                // Initial chess position pieces
                const pieces = {
                  "0-0": "♜", "0-1": "♞", "0-2": "♝", "0-3": "♛",
                  "0-4": "♚", "0-5": "♝", "0-6": "♞", "0-7": "♜",
                  "1-0": "♟", "1-1": "♟", "1-2": "♟", "1-3": "♟",
                  "1-4": "♟", "1-5": "♟", "1-6": "♟", "1-7": "♟",
                  "6-0": "♙", "6-1": "♙", "6-2": "♙", "6-3": "♙",
                  "6-4": "♙", "6-5": "♙", "6-6": "♙", "6-7": "♙",
                  "7-0": "♖", "7-1": "♘", "7-2": "♗", "7-3": "♕",
                  "7-4": "♔", "7-5": "♗", "7-6": "♘", "7-7": "♖"
                };
                
                const piece = pieces[`${row}-${col}`];
                const isWhitePiece = piece && "♔♕♖♗♘♙".includes(piece);
                
                return (
                  <div
                    key={i}
                    style={{ backgroundColor: isLight ? selectedTheme.light : selectedTheme.dark }}
                    className="aspect-square flex items-center justify-center"
                  >
                    {piece && (
                      <span className={`
                        text-2xl md:text-3xl chess-piece
                        ${isWhitePiece 
                          ? "text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]" 
                          : "text-zinc-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.3)]"
                        }
                      `}>
                        {piece}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {saving && (
        <div className="fixed inset-0 bg-background/50 flex items-center justify-center z-50">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
    </div>
  );
}
